type PageDataScope = {
  contextPath: string;
  language: string;
  siteUid: string;
  pathname?: string;
  search?: string;
};

function getPageDataScope(pathname?: string, search?: string): PageDataScope {
  return {
    contextPath: window.__SCHA_BOOT__?.contextPath ?? "",
    language: window.__SCHA_BOOT__?.language ?? "",
    siteUid: window.__SCHA_BOOT__?.siteUid ?? "",
    ...(pathname ? { pathname } : {}),
    ...(search ? { search } : {}),
  };
}

function getSharedDataScope(): Omit<PageDataScope, "pathname" | "search"> {
  const { contextPath, language, siteUid } = getPageDataScope();
  return { contextPath, language, siteUid };
}

export const pageDataKeys = {
  all: ["page-data"] as const,
  bootstrap: () => [...pageDataKeys.all, "bootstrap", getSharedDataScope()] as const,
  session: () => [...pageDataKeys.all, "session", getSharedDataScope()] as const,
  globalSlots: (userUid: string, accountKey: string) =>
    [...pageDataKeys.all, "global-slots", userUid, accountKey, getSharedDataScope()] as const,
  cart: (cartId = "") => [...pageDataKeys.all, "cart", cartId, getPageDataScope()] as const,
  cartRequest: (userPath: string, cartId = "") =>
    [...pageDataKeys.all, "cart/request", userPath, cartId, getPageDataScope()] as const,
  cartStoreFinder: (userPath: string, cartId = "") =>
    [...pageDataKeys.all, "cart/store-finder", userPath, cartId, getPageDataScope()] as const,
  checkoutSummary: (cartId = "") =>
    [...pageDataKeys.all, "checkout/multi/summary/view", cartId, getPageDataScope()] as const,
  home: () => [...pageDataKeys.all, "home", getPageDataScope()] as const,
  linearConfigurationRequestQuoteConfirmation: () =>
    [...pageDataKeys.all, "requestLinearConfigurationQuote/confirmation", getPageDataScope()] as const,
  lubricationSelectionAssistant: () =>
    [...pageDataKeys.all, "lubrication-selection-assistant", getPageDataScope()] as const,
  savedConfigurations: () => [...pageDataKeys.all, "saved-configs", getPageDataScope()] as const,
  scanDmc: () => [...pageDataKeys.all, "scandmc", getPageDataScope()] as const,
  storeFinder: () => [...pageDataKeys.all, "store-finder", getPageDataScope()] as const,
  storeFinderConfirmation: () => [...pageDataKeys.all, "store-finder/confirmation", getPageDataScope()] as const,
  mediaLibrary: () => [...pageDataKeys.all, "media-library", getPageDataScope()] as const,
  housingSelectionAssistant: () => [...pageDataKeys.all, "housingselectionassistant", getPageDataScope()] as const,
  selectionAssistant: () => [...pageDataKeys.all, "selectionassistant", getPageDataScope()] as const,
  abMonitor: () => [...pageDataKeys.all, "abmonitor", getPageDataScope()] as const,
  staticWelcome: () => [...pageDataKeys.all, "static-welcome", getPageDataScope()] as const,
  stc: () => [...pageDataKeys.all, "stc", getPageDataScope()] as const,
  contactUs: () => [...pageDataKeys.all, "contact-us", getPageDataScope()] as const,
  freeCapacityCockpit: () => [...pageDataKeys.all, "freecapacitycockpit", getPageDataScope()] as const,
  notifications: () => [...pageDataKeys.all, "notifications", getPageDataScope()] as const,
  notificationSettings: () => [...pageDataKeys.all, "notifications-settings", getPageDataScope()] as const,
  selectAccount: () => [...pageDataKeys.all, "selectaccount", getPageDataScope()] as const,
  productComparison: (productCode: string | null) =>
    [...pageDataKeys.all, "product-comparison", productCode ?? "", getSharedDataScope()] as const,
  registeredUser: () => [...pageDataKeys.all, "registered/user", getPageDataScope()] as const,
  registeredUserComplete: () => [...pageDataKeys.all, "registered/user/complete", getPageDataScope()] as const,
  registeredUserConfirmation: () => [...pageDataKeys.all, "registered/user/confirmation", getPageDataScope()] as const,
  quote: () => [...pageDataKeys.all, "quote", getPageDataScope()] as const,
  wishlist: () => [...pageDataKeys.all, "wishlist", getPageDataScope()] as const,
  category: (categoryCode: string, search = "") =>
    [...pageDataKeys.all, "category", categoryCode, getPageDataScope(undefined, search)] as const,
  pdp: (productCode: string, search = "") =>
    [...pageDataKeys.all, "pdp", productCode, getPageDataScope(undefined, search)] as const,
  plp: (categoryCode: string, search = "") =>
    [...pageDataKeys.all, "plp", categoryCode, getPageDataScope(undefined, search)] as const,
  catchAll: (lookupPath: string, pathname: string, search = "") =>
    [...pageDataKeys.all, "catch-all", lookupPath, getPageDataScope(pathname, search)] as const,
  configurator: (productCode: string, search = "") =>
    [...pageDataKeys.all, "configurator", productCode, getPageDataScope(undefined, search)] as const,
  embeddedProductConfigurator: (productCode: string, token: string | null) =>
    [...pageDataKeys.all, "embeddedProductConfigurator", productCode, token ?? "", getSharedDataScope()] as const,
  manageUsers: () => [...pageDataKeys.all, "manage-users", getPageDataScope()] as const,
  manageUserCreate: () => [...pageDataKeys.all, "manage-users-create", getPageDataScope()] as const,
  manageUserEdit: (user: string) => [...pageDataKeys.all, "manage-users-edit", user, getPageDataScope()] as const,
  bearingFrequencyCalculator: () => [...pageDataKeys.all, "bearing-frequency-calculator", getPageDataScope()] as const,
  myAccount: () => [...pageDataKeys.all, "my-account", getPageDataScope()] as const,
  accountSettings: () => [...pageDataKeys.all, "my-account/accountSettings", getPageDataScope()] as const,
  orderAndQuoteSettings: () => [...pageDataKeys.all, "my-account/orderAndQuoteSettings", getPageDataScope()] as const,
  contractOverview: () => [...pageDataKeys.all, "my-account/contractOverview", getPageDataScope()] as const,
  newContract: () => [...pageDataKeys.all, "my-account/new-contract", getPageDataScope()] as const,
  contractDetails: (contractCode: string, materialNumber: string, packCode: string, contractPosition: string) =>
    [
      ...pageDataKeys.all,
      `my-account/contractDetails/${contractCode}/${materialNumber}/${packCode}/${contractPosition}`,
      getPageDataScope(),
    ] as const,
  pageWithInputs: <T>(pageId: string, inputs: T) =>
    [...pageDataKeys.all, pageId, getSharedDataScope(), inputs] as const,
  page: (pageId: string) => [...pageDataKeys.all, pageId, getSharedDataScope()] as const,
};
